import { useGT } from 'gt-react';
import type {
  UploadRuleCheck,
  UploadRuleColumn,
  UploadRuleSeverity,
} from '@erp/rpc';

import { AS400_UPLOAD_FIELDS } from './as400UploadFields';
import type { RuleCheckType } from './ruleForm';
import { columnIndex } from './sheetCells';

/** The column letter and its AS400 field, e.g. `H — START`. */
export function uploadColumnLabel(column: UploadRuleColumn): string {
  const field = AS400_UPLOAD_FIELDS[columnIndex(column)];
  return field ? `${column} — ${field}` : column;
}

/**
 * Display text for upload rules: each upload column's AS400 field, a check read as a
 * sentence fragment, and the severities.
 */
export function useRuleText() {
  const gt = useGT();
  const checkTypeNames: Record<RuleCheckType, string> = {
    required: gt('Is required', { context: 'upload rule check type' }),
    equals: gt('Equals', { context: 'upload rule check type' }),
    oneOf: gt('Is one of', { context: 'upload rule check type' }),
    numberBetween: gt('Number between', { context: 'upload rule check type' }),
    maxLength: gt('Max length', { context: 'upload rule check type' }),
  };

  const severityNames: Record<UploadRuleSeverity, string> = {
    block: gt('Block', { context: 'upload rule severity: stops the download' }),
    warn: gt('Warn', {
      context: 'upload rule severity: download after confirming',
    }),
  };

  function describeRange(min: number | null, max: number | null): string {
    const ctx = { context: 'upload rule numeric range description' };
    if (min !== null && max !== null) {
      return gt('is between {min} and {max}', { min, max, ...ctx });
    }
    if (min !== null) return gt('is at least {min}', { min, ...ctx });
    return gt('is at most {max}', { max, ...ctx });
  }

  function describeCheck(check: UploadRuleCheck): string {
    const ctx = { context: 'upload rule check description' };
    switch (check.type) {
      case 'required':
        return gt('is not blank', ctx);
      case 'equals':
        return gt('equals {value}', { value: check.value, ...ctx });
      case 'oneOf':
        return gt('is one of {values}', {
          values: check.values.join(', '),
          ...ctx,
        });
      case 'numberBetween':
        return describeRange(check.min, check.max);
      case 'maxLength':
        return gt('is at most {max} characters', { max: check.max, ...ctx });
    }
  }

  return {
    columnLabel: uploadColumnLabel,
    checkTypeNames,
    severityNames,
    describeCheck,
  };
}
